import { useContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { ThemeContext } from 'styled-components';

import { status } from 'constants/general';
import companiesStrings from 'modules/companies/strings';
import Loader from 'modules/common/components/Loader';
import SectionDivider from 'modules/common/components/SectionDivider';

import {
  AddCompanyWrapper,
  FormWrapper,
  FormTitle,
  FormGrid,
  FormLabel,
  FormTextInput,
  FormTextAreaInput,
  FormFieldWrapper,
  FormValidationError,
  FormBtnWrapper,
  FormBtn,
} from './AddCompanyComponent.style';

const AddCompanyComponent = ({
  formFieldsValues,
  formFieldsValidation,
  shouldShowErrorMessage,
  handleTextInputFieldChange,
  handleAddOrEditCompanyBtnClick,
}) => {
  const { addOrEditCompany } = useSelector(state => ({
    addOrEditCompany: state.companies.addOrEditCompany,
  }));

  const themeContext = useContext(ThemeContext);
  const { colors } = themeContext;

  return (
    <AddCompanyWrapper>
      <FormWrapper>
        <FormTitle>{companiesStrings.addCompany.formTitle}</FormTitle>
        <SectionDivider gap={16} withSeparator separatorGap={16} borderColor={colors.grey004} />
        <FormGrid>
          {/* Name Field */}
          <FormFieldWrapper fullWidth>
            <FormLabel>{companiesStrings.addCompany.formNameFieldLabel}</FormLabel>
            <SectionDivider gap={8} />
            <FormTextInput
              type="text"
              value={formFieldsValues.name}
              maxLength="32"
              onChange={event => handleTextInputFieldChange('name', event)}
            />
            {formFieldsValidation.name === false && (
              <>
                <SectionDivider gap={4} />
                <FormValidationError>
                  {companiesStrings.addCompany.formFieldErrorText(
                    companiesStrings.addCompany.formNameFieldLabel,
                  )}
                </FormValidationError>
              </>
            )}
          </FormFieldWrapper>

          {/* Description Field */}
          <FormFieldWrapper fullWidth>
            <FormLabel>{companiesStrings.addCompany.formDescriptionFieldLabel}</FormLabel>
            <SectionDivider gap={8} />
            <FormTextInput
              type="text"
              value={formFieldsValues.description}
              maxLength="100"
              onChange={event => handleTextInputFieldChange('description', event)}
            />
            {formFieldsValidation.description === false && (
              <>
                <SectionDivider gap={4} />
                <FormValidationError>
                  {companiesStrings.addCompany.formFieldErrorText(
                    companiesStrings.addCompany.formDescriptionFieldLabel,
                  )}
                </FormValidationError>
              </>
            )}
          </FormFieldWrapper>

          {/* Long Description Field */}
          <FormFieldWrapper fullWidth>
            <FormLabel>{companiesStrings.addCompany.formLongDescriptionFieldLabel}</FormLabel>
            <SectionDivider gap={8} />
            <FormTextAreaInput
              type="text"
              value={formFieldsValues.longDescription}
              maxLength="1500"
              onChange={event => handleTextInputFieldChange('longDescription', event)}
            />
            {formFieldsValidation.longDescription === false && (
              <>
                <SectionDivider gap={4} />
                <FormValidationError>
                  {companiesStrings.addCompany.formFieldErrorText(
                    companiesStrings.addCompany.formLongDescriptionFieldLabel,
                  )}
                </FormValidationError>
              </>
            )}
          </FormFieldWrapper>

          {/* Email Field */}
          <FormFieldWrapper>
            <FormLabel>{companiesStrings.addCompany.formEmailFieldLabel}</FormLabel>
            <SectionDivider gap={8} />
            <FormTextInput
              type="text"
              value={formFieldsValues.email}
              onChange={event => handleTextInputFieldChange('email', event)}
            />
            {formFieldsValidation.email === false && (
              <>
                <SectionDivider gap={4} />
                <FormValidationError>
                  {companiesStrings.addCompany.formFieldErrorText(
                    companiesStrings.addCompany.formEmailFieldLabel,
                  )}
                </FormValidationError>
              </>
            )}
          </FormFieldWrapper>

          {/* Website Field */}
          <FormFieldWrapper>
            <FormLabel>{companiesStrings.addCompany.formWebsiteFieldLabel}</FormLabel>
            <SectionDivider gap={8} />
            <FormTextInput
              type="text"
              value={formFieldsValues.url}
              onChange={event => handleTextInputFieldChange('url', event)}
            />
            {formFieldsValidation.url === false && (
              <>
                <SectionDivider gap={4} />
                <FormValidationError>
                  {companiesStrings.addCompany.formFieldErrorText(
                    companiesStrings.addCompany.formWebsiteFieldLabel,
                  )}
                </FormValidationError>
              </>
            )}
          </FormFieldWrapper>
        </FormGrid>
        {shouldShowErrorMessage === true && (
          <>
            <SectionDivider gap={15} />
            <FormValidationError centerText>
              {companiesStrings.addCompany.requestErrorMessage}
            </FormValidationError>
          </>
        )}
        <SectionDivider gap={35} />
        <FormBtnWrapper>
          <FormBtn as="button" onClick={handleAddOrEditCompanyBtnClick}>
            {(addOrEditCompany.status === status.FETCHING && (
              <Loader width={14} height={14} thickness={4} />
            )) ||
              companiesStrings.addCompany.formAddBtnText}
          </FormBtn>
        </FormBtnWrapper>
      </FormWrapper>
    </AddCompanyWrapper>
  );
};

AddCompanyComponent.propTypes = {
  formFieldsValues: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    longDescription: PropTypes.string,
    email: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
  formFieldsValidation: PropTypes.shape({
    name: PropTypes.bool,
    description: PropTypes.bool,
    longDescription: PropTypes.bool,
    email: PropTypes.bool,
    url: PropTypes.bool,
  }).isRequired,
  shouldShowErrorMessage: PropTypes.bool.isRequired,
  handleTextInputFieldChange: PropTypes.func.isRequired,
  handleAddOrEditCompanyBtnClick: PropTypes.func.isRequired,
};

export default AddCompanyComponent;
